/**
 * The "While you were away" report's view-model (M4 spec §13, §15 item 7): a summary line, then Headlines,
 * Markets, Traffic and New stations, each with an empty line when there is nothing to say. M6 (plan P35): the fleet
 * section (`awayFleetModel`): the summary (earnings, wages, other costs, net, ships and captains lost, claims) and the
 * report's fleet lines grouped bad news first (losses, claim changes, captain news, earnings, events; awayFleet.json, D-459). M7 (plan
 * S35): the story section (`awayStoryModel`): the countdown warnings and the floors held past the absence (awayStory.json).
 * UI events only.
 */
import { z } from 'zod'
import type { AwayFleetLine, AwayFleetSummary, AwayReport, AwayStoryReport, StoryParty } from '../../strategic/types'
import fleetRaw from './awayFleet.json'
import storyRaw from './awayStory.json'
import { signedPct } from './causeModel'
import { factionName, wareName } from './names'
import { RAW_ID_PATTERN, wireRowNamed, wireSystemName, type SystemNameFn, type WireRow } from './wireModel'

export interface AwayMarketRow {
  ware: string
  station: string
  /** "120 → 150 cr/t". */
  prices: string
  /** "▲ +25%". */
  change: string
  sign: -1 | 0 | 1
}

export interface AwayReportModel {
  title: string
  /** "Away 2 h 14 min · 13 faction days simulated". */
  summary: string
  /** Shown when the away time hit the 8 h cap. */
  capped: string | null
  headlines: WireRow[]
  markets: AwayMarketRow[]
  traffic: { label: string; value: string }[]
  newStations: string[]
  empty: { headlines: string; markets: string; newStations: string }
  /** M6 (P35): the fleet section, null when the report has neither fleet lines nor a fleet summary. */
  fleet: AwayFleetModel | null
  /** M7 (S35): the story section (warnings, floors held), null when the absence touched no story term. */
  story: AwayStoryModel | null
}

const AWAY_LINE_KINDS = ['earning', 'loss', 'captain', 'claim', 'station', 'milestone'] as const satisfies readonly AwayFleetLine['kind'][]

const awayFleetConfigSchema = z.object({
  comment: z.string().optional(),
  title: z.string(),
  groups: z.array(z.object({ id: z.string(), title: z.string(), kinds: z.array(z.enum(AWAY_LINE_KINDS)).min(1) })).min(1),
  summary: z.object({
    earnings: z.string(),
    wages: z.string(),
    costs: z.string(),
    net: z.string(),
    shipsLost: z.string(),
    captainsLost: z.string(),
    claims: z.string(),
    claimGained: z.string().includes('{system}'),
    claimLost: z.string().includes('{system}').includes('{holder}'),
    claimLapsed: z.string().includes('{system}'),
    claimJoin: z.string(),
  }),
  empty: z.string(),
  credits: z.string(),
})
export type AwayFleetConfig = z.infer<typeof awayFleetConfigSchema>

let fleetConfig: AwayFleetConfig | null = null

/** awayFleet.json, checked (once for the bundled file). */
export function loadAwayFleetConfig(data: unknown = fleetRaw): AwayFleetConfig {
  if (data !== fleetRaw) return awayFleetConfigSchema.parse(data)
  fleetConfig ??= awayFleetConfigSchema.parse(data)
  return fleetConfig
}

export interface AwayFleetRow {
  label: string
  value: string
  /** up = a gain, down = a cost or loss. */
  tone: 'up' | 'down' | 'flat'
}

export interface AwayFleetGroup {
  id: string
  title: string
  lines: { key: string; text: string; day: number }[]
}

export interface AwayFleetModel {
  title: string
  summary: AwayFleetRow[]
  /** Groups with lines, in awayFleet.json order (the report's lines keep their newest-first order). */
  groups: AwayFleetGroup[]
  empty: string
}

function signedCredits(v: number, unit: string): string {
  const r = Math.round(v)
  const sign = r > 0 ? '+' : r < 0 ? '−' : ''
  return `${sign}${Math.abs(r).toLocaleString('en-US')} ${unit}`
}

function toneOf(v: number): 'up' | 'down' | 'flat' {
  const r = Math.round(v)
  return r > 0 ? 'up' : r < 0 ? 'down' : 'flat'
}

/** "+0 / −1 · lost Threshold to Tethys": the counts, then each claim by name (D-459 bug 3) when the summary carries them. */
function claimsValue(s: AwayFleetSummary, cfg: AwayFleetConfig): string {
  const counts = `+${round(s.claimsGained)} / −${round(s.claimsLost)}`
  const changes = s.claimChanges ?? []
  if (changes.length === 0) return counts
  const l = cfg.summary
  const named = changes.map((c) => {
    const t = c.change === 'gained' ? l.claimGained : c.holder.length > 0 ? l.claimLost : l.claimLapsed
    return t.replace('{system}', c.system).replace('{holder}', c.holder)
  })
  return `${counts} · ${named.join(l.claimJoin)}`
}

/** The away report's fleet section (M6 spec §8), null when the report says nothing about the fleet. */
export function awayFleetModel(report: Pick<AwayReport, 'fleet' | 'fleetSummary'>, cfg: AwayFleetConfig = loadAwayFleetConfig()): AwayFleetModel | null {
  const s = report.fleetSummary
  if (report.fleet.length === 0 && s === undefined) return null
  const l = cfg.summary
  const cr = cfg.credits
  const summary: AwayFleetRow[] =
    s === undefined
      ? []
      : [
          { label: l.earnings, value: signedCredits(s.earnings, cr), tone: toneOf(s.earnings) },
          { label: l.wages, value: signedCredits(s.wages, cr), tone: toneOf(s.wages) },
          { label: l.costs, value: signedCredits(s.costs, cr), tone: toneOf(s.costs) },
          { label: l.net, value: signedCredits(s.net, cr), tone: toneOf(s.net) },
          {
            label: l.shipsLost,
            value: s.shipsLost > 0 ? `${round(s.shipsLost)} (${signedCredits(-s.lossValue, cr)})` : '0',
            tone: s.shipsLost > 0 ? 'down' : 'flat',
          },
          { label: l.captainsLost, value: round(s.captainsLost), tone: s.captainsLost > 0 ? 'down' : 'flat' },
          { label: l.claims, value: claimsValue(s, cfg), tone: toneOf(s.claimsGained - s.claimsLost) },
        ]
  const groups: AwayFleetGroup[] = []
  for (const g of cfg.groups) {
    const kinds: readonly AwayFleetLine['kind'][] = g.kinds
    const lines: AwayFleetGroup['lines'] = []
    report.fleet.forEach((line, i) => {
      if (kinds.includes(line.kind) && line.text.length > 0) lines.push({ key: `${g.id}-${i}`, text: line.text, day: line.day })
    })
    if (lines.length > 0) groups.push({ id: g.id, title: g.title, lines })
  }
  return { title: cfg.title, summary, groups, empty: cfg.empty }
}

/** "2 h 14 min", "45 min", "under a minute". */
export function awayDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const m = Math.floor(s / 60)
  if (m < 1) return 'under a minute'
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rest = m % 60
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`
}

function round(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

/** A station id's system ("sys-01-st2" → "sys-01"), or null when the id has no system part. */
function stationSystemId(stationId: string): string | null {
  const i = stationId.lastIndexOf('-st')
  return i > 0 ? stationId.slice(0, i) : null
}

/**
 * `stationName` maps a station id to its name and `systemName` a system id to its name (the report carries ids). No raw
 * id reaches the text (H6, D-TBD-17): a station the lookup does not know reads "A station in <system>", and a new
 * station without a known system shows its name alone.
 */
export function awayReportModel(
  report: AwayReport,
  stationName: (id: string) => string | null = () => null,
  systemName?: SystemNameFn,
): AwayReportModel {
  const stationLabel = (id: string): string => {
    const name = stationName(id)
    if (name !== null && name.length > 0 && !RAW_ID_PATTERN.test(name)) return name
    const sys = stationSystemId(id)
    const sysName = sys === null ? null : wireSystemName(sys, systemName)
    return sysName === null ? 'Unknown station' : `A station in ${sysName}`
  }
  const days = Math.max(0, Math.round(report.simulatedDays))
  const markets: AwayMarketRow[] = report.markets.map((m) => {
    const pct = m.fromUnit > 0 ? (m.toUnit / m.fromUnit - 1) * 100 : 0
    const r = Math.round(pct)
    const sign: -1 | 0 | 1 = r > 0 ? 1 : r < 0 ? -1 : 0
    return {
      ware: wareName(m.ware),
      station: stationLabel(m.stationId),
      prices: `${round(m.fromUnit)} → ${round(m.toUnit)} cr/t`,
      change: `${sign > 0 ? '▲' : sign < 0 ? '▼' : '◆'} ${signedPct(pct)}`,
      sign,
    }
  })
  return {
    title: 'While you were away',
    summary: `Away ${awayDuration(report.awaySeconds)} · ${days} faction ${days === 1 ? 'day' : 'days'} simulated`,
    capped: report.capped ? 'The Reach caught up the last 8 hours only.' : null,
    headlines: report.headlines.map((h) => wireRowNamed(h, systemName)),
    markets,
    traffic: [
      { label: 'Ships built', value: round(report.traffic.built) },
      { label: 'Ships lost', value: round(report.traffic.lost) },
      { label: 'Convoys lost', value: round(report.traffic.convoysLost) },
    ],
    newStations: report.newStations.map((s) => {
      const sys = wireSystemName(s.systemId, systemName)
      return sys === null ? s.name : `${s.name} (${sys})`
    }),
    empty: {
      headlines: 'Quiet on the Wire.',
      markets: 'No price changes for your cargo or your last station.',
      newStations: 'No new stations.',
    },
    fleet: awayFleetModel(report),
    story: awayStoryModel(report.story),
  }
}

// ---------------------------------------------------------------------------------------------
// M7 (plan S35): the story section
// ---------------------------------------------------------------------------------------------

const awayStoryConfigSchema = z.object({
  comment: z.string().optional(),
  title: z.string(),
  warnings: z.string(),
  floors: z.string(),
  grievance: z.string(),
  grievanceYou: z.string(),
  aggression: z.string(),
  you: z.string(),
})
export type AwayStoryConfig = z.infer<typeof awayStoryConfigSchema>

let storyConfig: AwayStoryConfig | null = null

/** awayStory.json, checked (once for the bundled file). */
export function loadAwayStoryConfig(data: unknown = storyRaw): AwayStoryConfig {
  if (data !== storyRaw) return awayStoryConfigSchema.parse(data)
  storyConfig ??= awayStoryConfigSchema.parse(data)
  return storyConfig
}

export interface AwayStoryModel {
  title: string
  warningsTitle: string
  floorsTitle: string
  /** The countdown warnings (Okafor's and the like), as written. */
  warnings: string[]
  /** One line per floor held past the absence: who, never how long (no counters). */
  floors: { key: string; text: string }[]
}

function partyName(p: StoryParty, cfg: AwayStoryConfig): string {
  return p === 'player' ? cfg.you : factionName(p)
}

/** The away report's story section (M7 spec §8.2), null when there is nothing to say. */
export function awayStoryModel(story: AwayStoryReport | undefined, cfg: AwayStoryConfig = loadAwayStoryConfig()): AwayStoryModel | null {
  if (story === undefined || (story.warnings.length === 0 && story.floorsExtended.length === 0)) return null
  const floors = story.floorsExtended.map((f, i) => {
    if (f.kind === 'aggression') return { key: `aggression-${f.faction}-${i}`, text: cfg.aggression.replace('{faction}', factionName(f.faction)) }
    const you = f.a === 'player' || f.b === 'player'
    const other = f.a === 'player' ? f.b : f.a
    const text = you ? cfg.grievanceYou.replace('{a}', partyName(other, cfg)) : cfg.grievance.replace('{a}', partyName(f.a, cfg)).replace('{b}', partyName(f.b, cfg))
    return { key: `grievance-${f.a}-${f.b}-${i}`, text }
  })
  return { title: cfg.title, warningsTitle: cfg.warnings, floorsTitle: cfg.floors, warnings: [...story.warnings], floors }
}

/** The report with the story service's countdown warnings merged in (once each); the same report when none. */
export function withAwayStoryWarnings(report: AwayReport, warnings: readonly string[]): AwayReport {
  if (warnings.length === 0) return report
  const story = report.story ?? { floorsExtended: [], warnings: [] }
  const merged = [...story.warnings]
  for (const w of warnings) if (!merged.includes(w)) merged.push(w)
  return { ...report, story: { floorsExtended: [...story.floorsExtended], warnings: merged } }
}
